import { useMemo, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { TableCompData } from "../../../../components";
import InvestorPortalTableEmpty from "../../../../components/Admin/InvestorPortal/InvestorPortalTableEmpty";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import {
  OpportunityStatusBadge,
  RequestStatusBadge,
  KycStatusBadge,
  ParticipantTypeBadge,
} from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import InvestorPortalDetailHeader from "../../../../components/Admin/InvestorPortal/InvestorPortalDetailHeader";
import {
  DetailSection,
  DetailField,
  DetailFieldList,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalDetailFields";
import InvestorPortalActionButton, {
  InvestorPortalActionStack,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalActionButton";
import InvestorPortalTabNav from "../../../../components/Admin/InvestorPortal/InvestorPortalTabNav";
import InvestorPortalTableSection, {
  useClientPagination,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalTableSection";
import TableActionDropdown from "../../../../components/Admin/InvestorPortal/TableActionDropdown";
import AdminLifecycleStepper from "../../../../components/Admin/InvestorPortal/AdminLifecycleStepper";
import AdminPublicPreviewAction from "../../../../components/Admin/InvestorPortal/AdminPublicPreviewAction";
import AdminContentSectionEditor from "../../../../components/Admin/InvestorPortal/AdminContentSectionEditor";
import { FileUploaderComp } from "../../../../components/Ui";
import {
  getOpportunityLifecycleActions,
  isOpportunityEditable,
} from "../../../../store/admin/investorPortal/opportunityLifecycle";
import { DOCUMENT_VISIBILITY, UPDATE_STATUS } from "../../../../store/admin/investorPortal/constants";
import { formatMoney } from "../../../../utils/currencyFormat";
import { convertToTitleCase } from "../../../../utils/helpers";
import {
  useGetStaffOpportunityQuery,
  useListStaffInvestmentRequestsQuery,
  useStaffOpportunityLifecycleMutation,
  useUpdateStaffOpportunityMutation,
  useAddStaffOpportunityUpdateMutation,
  useAddStaffOpportunityDocumentMutation,
  useDeleteStaffOpportunityUpdateMutation,
  useDeleteStaffOpportunityDocumentMutation,
} from "../../../../store/admin/investorPortal/staffInvestmentsApi";
import {
  mapStaffOpportunityDetail,
  mapStaffInvestmentRequestListItem,
  mapStaffInvestmentRequestToParticipant,
  OPPORTUNITY_TARGET_TO_API_ACTION,
} from "../../../../utils/staffInvestmentsAdapter";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import moment from "moment";
import { withInvestorPortalGuard } from "../withInvestorPortalGuard";

const PER_PAGE = 10;

const CONTENT_TABS = [
  { key: "overview", label: "Overview" },
  { key: "content", label: "Content" },
  { key: "participants", label: "Participants" },
  { key: "requests", label: "Requests" },
  { key: "updates", label: "Updates" },
  { key: "documents", label: "Documents" },
  { key: "settings", label: "Settings" },
];

function paymentStatusLabel(status) {
  const map = {
    paid: "Paid",
    awaiting_confirmation: "Awaiting confirmation",
    pending_payment: "Pending payment",
    not_applicable: "—",
  };
  return map[status] || status;
}

function OpportunityDetailPage() {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "overview";
  const [confirm, setConfirm] = useState(null);
  const [contentDraft, setContentDraft] = useState(null);
  const [updateForm, setUpdateForm] = useState({
    title: "",
    body: "",
    status: UPDATE_STATUS.DRAFT,
    publishDate: "",
    visibility: "investors",
    imageUrl: "",
  });
  const [docForm, setDocForm] = useState({
    title: "",
    fileType: "PDF",
    fileUrl: "",
    fileName: "",
    visibility: DOCUMENT_VISIBILITY.INVESTORS,
    sortOrder: 0,
  });
  const [participantsPage, setParticipantsPage] = useState(1);
  const [requestsPage, setRequestsPage] = useState(1);
  const [lifecycleAction] = useStaffOpportunityLifecycleMutation();
  const [updateOpportunityApi] = useUpdateStaffOpportunityMutation();
  const [addUpdateApi] = useAddStaffOpportunityUpdateMutation();
  const [addDocumentApi] = useAddStaffOpportunityDocumentMutation();
  const [deleteUpdateApi] = useDeleteStaffOpportunityUpdateMutation();
  const [deleteDocumentApi] = useDeleteStaffOpportunityDocumentMutation();

  const { data: apiOpportunityRaw, isFetching, isError } = useGetStaffOpportunityQuery(opportunityId);
  const { data: apiRequestsData } = useListStaffInvestmentRequestsQuery({
    opportunity_id: opportunityId,
    per_page: 100,
  });

  const opportunity = useMemo(
    () => mapStaffOpportunityDetail(apiOpportunityRaw),
    [apiOpportunityRaw]
  );

  const apiRequestItems = useMemo(
    () => (apiRequestsData?.items || []).map(mapStaffInvestmentRequestListItem).filter(Boolean),
    [apiRequestsData?.items]
  );

  const participants = useMemo(
    () =>
      apiRequestItems
        .filter((r) => ["allocated", "approved"].includes(r.status))
        .map((r) => mapStaffInvestmentRequestToParticipant(r, opportunity))
        .filter(Boolean),
    [apiRequestItems, opportunity]
  );

  const requests = apiRequestItems;

  const updates = useMemo(() => opportunity?.updates || [], [opportunity?.updates]);

  const documents = useMemo(
    () => [...(opportunity?.documents || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [opportunity?.documents]
  );

  const { pageData: participantsPageData, pagination: participantsPagination } = useClientPagination(
    participants,
    { page: participantsPage, perPage: PER_PAGE, setPage: setParticipantsPage }
  );

  const { pageData: requestsPageData, pagination: requestsPagination } = useClientPagination(requests, {
    page: requestsPage,
    perPage: PER_PAGE,
    setPage: setRequestsPage,
  });

  if (isFetching) {
    return (
      <div className="pb-4">
        <TableSkeletonLoader rows={10} columns={2} />
      </div>
    );
  }

  if (!opportunity || isError) {
    return <Navigate to="/super_admin/investor_portal/investments/opportunities" replace />;
  }

  const contentValues = contentDraft || opportunity;
  const progressPct = opportunity.fundingTargetNgn
    ? Math.round((opportunity.fundedAmountNgn / opportunity.fundingTargetNgn) * 100)
    : 0;

  const returnExposure = requests
    .filter((r) => ["approved", "allocated"].includes(r.status))
    .reduce((s, r) => s + r.totalNgn * (opportunity.expectedReturnPct / 100), 0);

  const lifecycleActions = getOpportunityLifecycleActions(opportunity.status);

  const runLifecycleAction = (action) => {
    if (action.action === "preview") return;
    const isDestructive = ["archived", "closed"].includes(action.target);
    setConfirm({
      title: action.label,
      message: `Are you sure you want to ${action.label.toLowerCase()} "${opportunity.title}"?${
        action.target === "archived" ? " Archived opportunities are removed from the investor marketplace." : ""
      }`,
      confirmText: "Yes, continue",
      confirmButtonClass: isDestructive ? "bg-danger text-white" : "bg-primary1 text-white",
      action: async () => {
        const apiAction = action.apiAction || OPPORTUNITY_TARGET_TO_API_ACTION[action.target];
        if (!apiAction) {
          toast.error("This lifecycle action is not available via API yet.");
          setConfirm(null);
          return;
        }
        try {
          await lifecycleAction({ id: opportunity.id, action: apiAction }).unwrap();
          toast.success(action.label);
        } catch (error) {
          toast.error(error?.data?.message || `Unable to ${action.label.toLowerCase()}.`);
        }
        setConfirm(null);
      },
    });
  };

  const confirmDeleteUpdate = (update) => {
    setConfirm({
      title: "Delete update",
      message: `Remove "${update.title || "this update"}"? This cannot be undone.`,
      confirmText: "Yes, delete",
      confirmButtonClass: "bg-danger text-white",
      action: async () => {
        try {
          await deleteUpdateApi({ id: opportunity.id, updateId: update.id }).unwrap();
          toast.success("Update deleted");
        } catch (error) {
          toast.error(error?.data?.message || "Unable to delete update.");
        }
        setConfirm(null);
      },
    });
  };

  const confirmDeleteDocument = (doc) => {
    setConfirm({
      title: "Remove document",
      message: `Remove "${doc.title || "this document"}"? Investors will no longer see it on the opportunity page.`,
      confirmText: "Yes, remove",
      confirmButtonClass: "bg-danger text-white",
      action: async () => {
        try {
          await deleteDocumentApi({
            id: opportunity.id,
            documentId: doc.id,
          }).unwrap();
          toast.success("Document removed");
        } catch (error) {
          toast.error(error?.data?.message || "Unable to remove document.");
        }
        setConfirm(null);
      },
    });
  };

  const goToTab = (key) => setSearchParams({ tab: key });

  const participantColumns = [
    { name: "Name", selector: (r) => r.name, minWidth: "130px", grow: 1 },
    { name: "Type", cell: (r) => <ParticipantTypeBadge type={r.participantType} />, minWidth: "150px" },
    { name: "Units", selector: (r) => r.units, minWidth: "70px", center: true },
    { name: "Committed", selector: (r) => formatMoney(r.committedAmount), minWidth: "125px", right: true },
    { name: "Request", cell: (r) => <RequestStatusBadge status={r.requestStatus} />, minWidth: "115px" },
    { name: "KYC", cell: (r) => <KycStatusBadge status={r.kycStatus} />, minWidth: "95px" },
    { name: "Payment", selector: (r) => paymentStatusLabel(r.paymentStatus), minWidth: "130px" },
    {
      name: "Next payout",
      selector: (r) => (r.nextPayoutDate ? moment(r.nextPayoutDate).format("DD-MMM-YY") : "—"),
      minWidth: "105px",
    },
    {
      name: "Actions",
      cell: (r) => (
        <TableActionDropdown
          items={[
            {
              name: "View request",
              action: () =>
                navigate(
                  `/super_admin/investor_portal/investments/requests/${encodeURIComponent(
                    r.reference || r.requestId
                  )}`
                ),
            },
          ]}
          row={r}
        />
      ),
      width: "80px",
      center: true,
    },
  ];

  const requestColumns = [
    { name: "ID", selector: (r) => r.id, minWidth: "90px" },
    { name: "Participant", selector: (r) => r.participantName, minWidth: "140px", grow: 1 },
    { name: "Units", selector: (r) => r.units, minWidth: "70px", center: true },
    { name: "Amount", selector: (r) => formatMoney(r.totalNgn), minWidth: "125px", right: true },
    { name: "Status", cell: (r) => <RequestStatusBadge status={r.status} />, minWidth: "115px" },
    {
      name: "Actions",
      cell: (r) => (
        <TableActionDropdown
          items={[
            {
              name: "View details",
              action: () =>
                navigate(
                  `/super_admin/investor_portal/investments/requests/${encodeURIComponent(
                    r.reference || r.id
                  )}`
                ),
            },
          ]}
          row={r}
        />
      ),
      width: "80px",
      center: true,
    },
  ];

  return (
    <div className="pb-4">
      <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-between gap-3 mb-3">
        <InvestorPortalDetailHeader
          title=""
          highlight={opportunity.title}
          onBack={() => navigate("/super_admin/investor_portal/investments/opportunities")}
        />
        <div className="d-flex flex-wrap gap-2">
          <AdminPublicPreviewAction opportunity={opportunity} fullWidth={false} />
          {isOpportunityEditable(opportunity.status) ? (
            <InvestorPortalActionButton
              btnText="Edit opportunity"
              variant="primary"
              fullWidth={false}
              onClick={() =>
                navigate(
                  `/super_admin/investor_portal/investments/opportunities/${opportunityId}/edit`
                )
              }
            />
          ) : null}
        </div>
      </div>

      <div className="row g-3 investor-portal-detail-layout mb-3">
        <div className="col-lg-8">
          <DetailSection>
            <div className="investor-portal-status-row">
              <OpportunityStatusBadge status={opportunity.status} />
              <span className="investor-portal-category-badge">{opportunity.category}</span>
            </div>
            <p className="text-muted small mb-3">{opportunity.shortDescription}</p>
            {opportunity.canAcceptRequests ? (
              <div
                className="rounded-3 px-3 py-2 mb-3 small"
                style={{ background: "rgba(25, 188, 49, 0.08)", border: "1px solid rgba(25, 188, 49, 0.25)", color: "#1f6b2e" }}
              >
                Open for investment — investors can submit and pay.
              </div>
            ) : (
              <div
                className="rounded-3 px-3 py-3 mb-3"
                style={{ background: "rgba(255, 149, 0, 0.08)", border: "1px solid rgba(255, 149, 0, 0.3)" }}
              >
                <p className="fw-semibold mb-1" style={{ color: "#383838", fontSize: 14 }}>
                  Not open for investment
                </p>
                <p className="small mb-0" style={{ color: "#525252" }}>
                  {opportunity.notReadyReason ||
                    "This opportunity is not currently accepting investment requests."}
                </p>
                {opportunity.suggestedAction?.apiAction ||
                opportunity.suggestedAction?.uiAction === "edit" ? (
                  <div className="mt-2">
                    <InvestorPortalActionButton
                      btnText={opportunity.suggestedAction.label || "Fix now"}
                      variant="primary"
                      fullWidth={false}
                      onClick={() => {
                        if (opportunity.suggestedAction?.uiAction === "edit") {
                          navigate(
                            `/super_admin/investor_portal/investments/opportunities/${opportunityId}/edit`
                          );
                          return;
                        }
                        runLifecycleAction({
                          label: opportunity.suggestedAction.label || "Fix now",
                          apiAction: opportunity.suggestedAction.apiAction,
                          target: opportunity.suggestedAction.apiAction === "reopen-funding"
                            ? "funding"
                            : "published",
                        });
                      }}
                    />
                  </div>
                ) : null}
              </div>
            )}
            <AdminLifecycleStepper status={opportunity.status} />
            <div className="progress rounded-pill mb-2 mt-3" style={{ height: 10, maxWidth: 420 }}>
              <div className="progress-bar" style={{ width: `${progressPct}%`, backgroundColor: "var(--color-primary)" }} />
            </div>
            <p className="small mb-0" style={{ color: "#45464e" }}>
              <strong>{progressPct}%</strong> funded — {formatMoney(opportunity.fundedAmountNgn)} of {formatMoney(opportunity.fundingTargetNgn)}
            </p>
          </DetailSection>
        </div>

        <div className="col-lg-4">
          <DetailSection title="Lifecycle actions">
            <InvestorPortalActionStack>
              {lifecycleActions.map((action) =>
                action.action === "preview" ? (
                  <AdminPublicPreviewAction key={action.label} opportunity={opportunity} variant={action.variant} />
                ) : (
                  <InvestorPortalActionButton
                    key={action.label}
                    btnText={action.label}
                    variant={action.variant}
                    onClick={() => runLifecycleAction(action)}
                  />
                )
              )}
            </InvestorPortalActionStack>
          </DetailSection>
        </div>
      </div>

      <InvestorPortalTabNav tabs={CONTENT_TABS} activeKey={tab} onChange={goToTab} />

      {tab === "overview" && (
        <div className="row g-3">
          <div className="col-md-6">
            <DetailSection title="Financial metrics">
              <DetailFieldList>
                <DetailField label="Unit price" value={formatMoney(opportunity.unitPriceNgn)} />
                <DetailField label="Expected return" value={`${opportunity.expectedReturnPct}%`} />
                <DetailField label="Duration" value={`${opportunity.durationMonths} months`} />
                <DetailField label="Return exposure" value={formatMoney(Math.round(returnExposure))} />
                <DetailField label="Participants" value={opportunity.participantCount} />
                <DetailField label="Available units" value={opportunity.availableUnits} />
              </DetailFieldList>
            </DetailSection>
          </div>
          <div className="col-md-6">
            <DetailSection title="Key dates">
              <DetailFieldList>
                <DetailField label="Funding opens" value={opportunity.fundingOpenDate ? moment(opportunity.fundingOpenDate).format("DD-MMM-YYYY") : "—"} />
                <DetailField label="Funding closes" value={opportunity.fundingCloseDate ? moment(opportunity.fundingCloseDate).format("DD-MMM-YYYY") : "—"} />
                <DetailField label="Maturity" value={opportunity.maturityDate ? moment(opportunity.maturityDate).format("DD-MMM-YYYY") : "—"} />
                <DetailField label="Scheduled publish" value={opportunity.scheduledPublishAt ? moment(opportunity.scheduledPublishAt).format("DD-MMM-YYYY") : "—"} />
              </DetailFieldList>
            </DetailSection>
          </div>
        </div>
      )}

      {tab === "content" && (
        <DetailSection title="Public page content">
          <p className="text-muted small mb-3">Edit sections that appear on the investor-facing detail page. Save, then use Preview public page.</p>
          <AdminContentSectionEditor values={contentValues} onChange={setContentDraft} />
          <div className="d-flex flex-wrap gap-2 mt-3">
            <InvestorPortalActionButton
              btnText="Save content"
              variant="primary"
              fullWidth={false}
              onClick={async () => {
                try {
                  await updateOpportunityApi({
                    id: opportunity.id,
                    body: {
                      public_content: {
                        overview: contentValues.overview,
                        fundsUsage: contentValues.fundsUsage,
                        highlights: contentValues.highlights,
                        howItWorks: contentValues.howItWorks,
                        gallery: contentValues.gallery,
                        milestones: contentValues.milestones,
                        faqs: contentValues.faqs,
                        terms: contentValues.terms,
                        riskDisclaimer: contentValues.riskDisclaimer,
                      },
                    },
                  }).unwrap();
                  setContentDraft(null);
                  toast.success("Content saved");
                } catch (error) {
                  toast.error(error?.data?.message || "Unable to save content.");
                }
              }}
            />
            <AdminPublicPreviewAction opportunity={contentValues} fullWidth={false} />
          </div>
        </DetailSection>
      )}

      {tab === "participants" && (
        <InvestorPortalTableSection pagination={participantsPagination}>
          <TableCompData columns={participantColumns} data={participantsPageData} noDataComponent={<InvestorPortalTableEmpty variant="empty" title="No participants yet." description="Allocated investors for this opportunity will appear here." />} />
        </InvestorPortalTableSection>
      )}

      {tab === "requests" && (
        <InvestorPortalTableSection pagination={requestsPagination}>
          <TableCompData columns={requestColumns} data={requestsPageData} noDataComponent={<InvestorPortalTableEmpty variant="empty" title="No requests for this opportunity." description="Investment requests linked to this product will appear here." />} />
        </InvestorPortalTableSection>
      )}

      {tab === "updates" && (
        <>
          <DetailSection title="Post an update">
            <div className="row g-2 mb-2">
              <div className="col-md-6">
                <input className="form-control investor-portal-note-input" placeholder="Title" value={updateForm.title} onChange={(e) => setUpdateForm({ ...updateForm, title: e.target.value })} />
              </div>
              <div className="col-md-3">
                <select className="form-select investor-portal-note-input" value={updateForm.status} onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}>
                  {Object.values(UPDATE_STATUS).map((s) => (
                    <option key={s} value={s}>{convertToTitleCase(s)}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <input type="date" className="form-control investor-portal-note-input" value={updateForm.publishDate} onChange={(e) => setUpdateForm({ ...updateForm, publishDate: e.target.value })} />
              </div>
            </div>
            <textarea className="form-control investor-portal-note-input mb-2" rows={3} placeholder="Body" value={updateForm.body} onChange={(e) => setUpdateForm({ ...updateForm, body: e.target.value })} />
            <input className="form-control investor-portal-note-input mb-3" placeholder="Image URL (optional)" value={updateForm.imageUrl} onChange={(e) => setUpdateForm({ ...updateForm, imageUrl: e.target.value })} />
            <InvestorPortalActionButton
              btnText="Save update"
              variant="primary"
              fullWidth={false}
              onClick={async () => {
                if (!updateForm.title) return toast.error("Title required");
                try {
                  await addUpdateApi({
                    id: opportunity.id,
                    body: {
                      title: updateForm.title,
                      body: updateForm.body,
                      status: updateForm.status,
                      visibility: updateForm.visibility === "investors" ? "public" : "private",
                      image: updateForm.imageUrl || null,
                    },
                  }).unwrap();
                  setUpdateForm({ title: "", body: "", status: UPDATE_STATUS.DRAFT, publishDate: "", visibility: "investors", imageUrl: "" });
                  toast.success("Update saved");
                } catch (error) {
                  toast.error(error?.data?.message || "Unable to save update.");
                }
              }}
            />
          </DetailSection>
          {updates.length === 0 ? (
            <InvestorPortalTableEmpty
              variant="empty"
              title="No updates yet."
              description="Post progress updates for staff or investors."
            />
          ) : (
            updates.map((u) => (
            <DetailSection key={u.id}>
              <div className="d-flex justify-content-between gap-3 flex-wrap">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h6 className="fw-bold mb-0">{u.title}</h6>
                    <span className="investor-portal-category-badge">{convertToTitleCase(u.status || "draft")}</span>
                    {u.investorVisible ? <span className="badge bg-success">Investors see this</span> : <span className="badge bg-secondary">Staff only</span>}
                  </div>
                  <p className="small text-muted mb-2">{moment(u.publishedAt || u.createdAt).format("DD-MMM-YYYY")}</p>
                  <p className="small mb-0">{u.body}</p>
                </div>
                <InvestorPortalActionButton
                  btnText="Delete"
                  variant="danger"
                  fullWidth={false}
                  onClick={() => confirmDeleteUpdate(u)}
                />
              </div>
            </DetailSection>
          ))
          )}
        </>
      )}

      {tab === "documents" && (
        <>
          <DetailSection title="Documents">
            <p className="text-muted small mb-3">
              Upload offering documents (PDF, Word, Excel, or images). Investors see public/investors
              visibility items on the detail page.
            </p>
            <div className="row g-3 align-items-end mb-3">
              <div className="col-md-6">
                <FileUploaderComp
                  LabelText="Document file"
                  required
                  accept="documents"
                  bodyText={
                    <span className="btn btn-outline-secondary btn-sm">
                      {docForm.fileUrl ? "Replace file" : "Choose file to upload"}
                    </span>
                  }
                  onChange={(url, file) => {
                    const name = file?.name || "";
                    const ext = name.includes(".")
                      ? name.split(".").pop().toUpperCase()
                      : docForm.fileType;
                    setDocForm((prev) => ({
                      ...prev,
                      fileUrl: url || "",
                      fileName: name || prev.fileName,
                      fileType: prev.fileType || ext || "PDF",
                      title: prev.title || name.replace(/\.[^.]+$/, "") || prev.title,
                    }));
                    if (url) toast.success("File uploaded");
                  }}
                />
                {docForm.fileUrl ? (
                  <div className="small mt-2">
                    <a href={docForm.fileUrl} target="_blank" rel="noreferrer">
                      {docForm.fileName || "View uploaded file"}
                    </a>
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger px-2"
                      onClick={() =>
                        setDocForm((prev) => ({ ...prev, fileUrl: "", fileName: "" }))
                      }
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <p className="text-muted small mb-0 mt-1">
                    PDF, DOC, DOCX, XLS, XLSX, JPG or PNG.
                  </p>
                )}
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold">Title</label>
                <input
                  className="form-control investor-portal-note-input"
                  placeholder="Title"
                  value={docForm.title}
                  onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold">Type</label>
                <input
                  className="form-control investor-portal-note-input"
                  placeholder="Type"
                  value={docForm.fileType}
                  onChange={(e) => setDocForm({ ...docForm, fileType: e.target.value })}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold">Visibility</label>
                <select
                  className="form-select investor-portal-note-input"
                  value={docForm.visibility}
                  onChange={(e) => setDocForm({ ...docForm, visibility: e.target.value })}
                >
                  {Object.values(DOCUMENT_VISIBILITY).map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-semibold">Sort</label>
                <input
                  type="number"
                  className="form-control investor-portal-note-input"
                  placeholder="Sort"
                  value={docForm.sortOrder}
                  onChange={(e) =>
                    setDocForm({ ...docForm, sortOrder: Number(e.target.value) })
                  }
                />
              </div>
              <div className="col-md-3">
                <InvestorPortalActionButton
                  btnText="Add document"
                  variant="primary"
                  fullWidth={false}
                  onClick={async () => {
                    if (!docForm.title?.trim()) return toast.error("Title is required");
                    if (!docForm.fileUrl) return toast.error("Upload a document file first");
                    try {
                      await addDocumentApi({
                        id: opportunity.id,
                        body: {
                          title: docForm.title.trim(),
                          document_type: docForm.fileType || "PDF",
                          file_url: docForm.fileUrl,
                          file_path: docForm.fileUrl,
                          file_name: docForm.fileName || docForm.title.trim(),
                          visibility: docForm.visibility,
                          sort_order: docForm.sortOrder || documents.length,
                        },
                      }).unwrap();
                      setDocForm({
                        title: "",
                        fileType: "PDF",
                        fileUrl: "",
                        fileName: "",
                        visibility: DOCUMENT_VISIBILITY.INVESTORS,
                        sortOrder: 0,
                      });
                      toast.success("Document added");
                    } catch (error) {
                      toast.error(error?.data?.message || "Unable to add document.");
                    }
                  }}
                />
              </div>
            </div>
          </DetailSection>
          <InvestorPortalTableSection>
            <TableCompData
              columns={[
                { name: "Title", selector: (d) => d.title, minWidth: "140px", grow: 1 },
                { name: "Type", selector: (d) => d.fileType, minWidth: "70px" },
                {
                  name: "Visibility",
                  cell: (d) =>
                    d.visibility === DOCUMENT_VISIBILITY.PUBLIC
                      ? "Public"
                      : d.visibility === DOCUMENT_VISIBILITY.INVESTORS
                        ? "Investors"
                        : "Private",
                  minWidth: "90px",
                },
                { name: "Sort", selector: (d) => d.sortOrder ?? 0, minWidth: "60px", center: true },
                {
                  name: "Preview",
                  cell: (d) =>
                    d.url ? (
                      <a href={d.url} target="_blank" rel="noreferrer" className="small">
                        View
                      </a>
                    ) : (
                      <span className="text-muted small">—</span>
                    ),
                  minWidth: "120px",
                },
                {
                  name: "Actions",
                  cell: (d) => (
                    <TableActionDropdown
                      items={[
                        {
                          name: "Remove",
                          action: () => confirmDeleteDocument(d),
                        },
                      ]}
                      row={d}
                    />
                  ),
                  width: "80px",
                  center: true,
                },
              ]}
              data={documents}
              noDataComponent={
                <InvestorPortalTableEmpty
                  variant="empty"
                  title="No documents yet."
                  description="Add offering documents for staff or investors."
                />
              }
            />
          </InvestorPortalTableSection>
        </>
      )}

      {tab === "settings" && (
        <DetailSection title="Opportunity settings">
          <DetailFieldList>
            <DetailField label="Slug" value={opportunity.slug} />
            <DetailField label="Risk level" value={opportunity.riskLevel} />
            <DetailField label="Min / max units" value={`${opportunity.minUnits} – ${opportunity.maxUnits}`} />
            <DetailField label="Payout type" value={opportunity.payoutType?.replace(/_/g, " ")} />
            <DetailField label="Created by" value={opportunity.createdByName || "—"} />
            <DetailField label="Last updated" value={moment(opportunity.updatedAt).format("DD-MMM-YYYY HH:mm")} />
          </DetailFieldList>
          <div className="mt-3">
            <InvestorPortalActionButton
              btnText="Open full editor"
              variant="outline"
              fullWidth={false}
              onClick={() => navigate(`/super_admin/investor_portal/investments/opportunities/${opportunityId}/edit`)}
            />
          </div>
        </DetailSection>
      )}

      <ConfirmationModal
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.action}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText || "Yes, continue"}
        cancelText="Cancel"
        confirmButtonClass={confirm?.confirmButtonClass || "bg-primary1 text-white"}
      />
    </div>
  );
}

export default withInvestorPortalGuard(OpportunityDetailPage, "investment");
